/**
 * /competitive-analysis-template — SEO landing page targeting the
 * "competitive analysis template" / "competitor analysis template" /
 * "competitive analysis example" cluster.
 *
 * Differentiation angle: a competitive analysis template in a doc or a
 * slide is fill-in-the-blanks — and the market it describes moves the
 * week after you finish it. Competitors reprice, reposition, ship; the
 * analysis becomes a fossil nobody trusts. This page hands the reader the
 * actual template (the eight fields, spelled out) so a searcher gets
 * something usable, then shows that Draftlize turns each competitor
 * observation into a structured card linked to your positioning decision —
 * so when a rival moves, the cards that rested on the old read auto-flag
 * stale.
 *
 * English-only + inline (no i18n), mirroring DecisionLogTemplate.tsx and
 * the comparison pages. Chinese variant lives in
 * CompetitiveAnalysisTemplateZh.tsx.
 */
import { MarketingShell } from './MarketingShell';
import { TemplateCta } from './TemplateCta';

export function CompetitiveAnalysisTemplate() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/competitive-analysis-template"
      documentTitle="Competitive Analysis Template (That Stays Current) · Draftlize"
      documentDescription="A competitive analysis template you can copy — Competitor, Dimensions compared, Their strengths, Their weaknesses, Their positioning & pricing, Our differentiation, Threats & opportunities, Last verified. The catch: a competitive analysis template in a doc or slide is out of date the week you finish it, because competitors reprice and reposition. In Draftlize every competitor observation is a structured card linked to your positioning decision, and when a rival moves the dependent cards auto-flag stale."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Template · Competitive Analysis</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            A competitive analysis template<br/>
            <em>that doesn't go out of date the week you finish it.</em>
          </h1>
          <p className="lede">
            You came for a <em>competitive analysis template</em> — the columns, the headings, something to drop into a doc or a slide before the next strategy review. It's below; take it. But know what you're buying: a competitive analysis is a snapshot of a moving market, and the market moves the week after you finish. A rival reprices, reframes its positioning, ships the feature you called a gap — and your beautifully formatted table is now quietly wrong, with no way to tell which rows. Draftlize gives you the same eight fields — and makes each one a card an AI agent reads and writes, linked to the positioning decision it informs, so when a competitor moves, everything that rested on the old read flags itself stale.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">The template</span>
            <h2 className="serif">Eight fields, <em>per competitor.</em></h2>
          </div>
          <p>This is the whole template — fill one block in for each competitor you track. It works in a doc, a wiki, a spreadsheet, or a slide. Use it as-is; it's a perfectly good <em>competitor analysis template</em>. The next section is about what changes when each field stops being text and becomes a card that knows when it's gone stale.</p>
        </div>

        <div
          style={{
            border: '1px solid var(--ink-0)',
            background: 'var(--paper-0)',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {[
            {
              k: 'Competitor',
              hint: 'Who',
              body: 'The one you\'re sizing up, named plainly. Direct rival, adjacent tool, or the "good enough" status quo (a spreadsheet, doing nothing) — the last is the competitor most analyses forget to list.',
            },
            {
              k: 'Dimensions compared',
              hint: 'The axes',
              body: 'The criteria you\'re judging on — the ones that decide a deal, not a feature checklist. Pick the four or five that actually move a buyer; a comparison across twenty dimensions hides the two that matter.',
            },
            {
              k: 'Their strengths',
              hint: 'What they win on',
              body: 'Where they genuinely beat you, stated without flinching. The honest version is the only useful version — a competitive analysis written to make you feel good is a competitive analysis you can\'t act on.',
            },
            {
              k: 'Their weaknesses',
              hint: 'Where they\'re soft',
              body: 'The gaps you can press: a missing capability, a clumsy workflow, a segment they underserve. Note whether each is structural (hard for them to fix) or temporary (shipping next quarter) — it changes everything.',
            },
            {
              k: 'Their positioning & pricing',
              hint: 'The pitch + the number',
              body: 'How they frame themselves and what they charge — the headline claim, the model (per seat, per use, flat), the published price. This is the field that rots fastest, because pricing pages change without telling you.',
            },
            {
              k: 'Our differentiation',
              hint: 'Why us',
              body: 'The one thing you do that they can\'t easily copy, against this specific competitor. Not a feature list — the wedge. If you can\'t name it per rival, the analysis just surfaced a real strategy problem.',
            },
            {
              k: 'Threats & opportunities',
              hint: 'So what',
              body: 'The move this comparison implies: a threat to defend against, an opening to take. An analysis with no "therefore" is trivia. This is the field a reader actually opens the document to find.',
            },
            {
              k: 'Last verified',
              hint: 'When',
              body: 'The date you last confirmed any of the above against reality. The single most important field and the one every template omits — without it, you can\'t tell a current read from a fossil.',
            },
          ].map((f, i) => (
            <div
              key={f.k}
              style={{
                display: 'grid',
                gridTemplateColumns: '220px 1fr',
                gap: 24,
                padding: '20px 22px',
                borderTop: i === 0 ? 0 : '1px dashed var(--rule)',
                alignItems: 'baseline',
              }}
            >
              <div>
                <span
                  className="serif"
                  style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2 }}
                >
                  {f.k}
                </span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9.5,
                    letterSpacing: '.1em',
                    textTransform: 'uppercase',
                    color: 'var(--amber)',
                    fontWeight: 700,
                  }}
                >
                  {f.hint}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>

        <p
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            letterSpacing: '.06em',
            color: 'var(--ink-3)',
            textAlign: 'center',
            marginTop: 18,
          }}
        >
          That's the template. Below: why the market makes it obsolete the week you finish — and what we do about it.
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Why a competitive analysis <em>goes stale.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">It's a photograph of a moving subject</h3>
            <p>A competitive analysis captures rivals on the day you wrote it. But competitors don't hold still — they reprice, rebrand, ship. The "Last verified" date you (probably didn't) record is the only thing standing between a current read and a fossil, and a doc has no way to age a row or warn you it's stale.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Pricing and positioning are the first to lie</h3>
            <p>The "Their positioning & pricing" field decays fastest of all. A competitor quietly changes its pricing page and your slide still shows last quarter's number — so the differentiation you built on it, and the deal you're trying to win with it, are both running on stale intel nobody flagged.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Finished is the high-water mark</h3>
            <p>The analysis is most accurate the minute the strategy review ends, and never that accurate again. By next quarter a real fraction is wrong, and you can't tell which fraction — so the whole document gets quietly distrusted, which is worse than not having one. It gets rebuilt from scratch instead of updated.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">The same template, <em>made live.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Each observation becomes a card, not a cell</h3>
            <p>In Draftlize the template isn't a static table — every competitor observation is a typed card with those exact fields: Competitor, Dimensions, Strengths, Weaknesses, Positioning & pricing, Differentiation, Threats & opportunities, Last verified. Structured, addressable by ID, and citable from a positioning decision or a thread instead of buried in a slide deck.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">A rival moves, dependent cards flag stale</h3>
            <p>Link each competitor card to the positioning decision it informs. When a competitor reprices or repositions and you update its card, every decision and spec that rested on the old read turns stale automatically — the way a build system invalidates everything downstream of a changed file. Your differentiation can't keep quietly resting on a number that's no longer true.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">An AI agent reads and writes it</h3>
            <p>Claude Code or Cursor, over MCP, reads every relevant competitor card before it drafts your positioning, your launch copy, or the next spec — and writes new observations back into the same shape. The analysis stops being a document for a reviewer who never returns and becomes context that gets read on every turn.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          A competitive analysis template tells you what to write down about a rival. It can't tell you when the rival moved and made it wrong.
        </blockquote>
        <cite>Keep the eight fields. Let the substrate keep them current.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'What is a competitive analysis?', a: 'A competitive analysis is a structured comparison of your product against rivals across the dimensions that matter — strengths, weaknesses, positioning, pricing, and differentiation — so you can see where you genuinely win and where a competitor threatens you. Its job is to inform positioning and strategy, not to collect logos.' },
            { q: 'What should a competitive analysis template include?', a: 'For each competitor: the dimensions you compare on, their strengths and weaknesses, their positioning and pricing, your differentiation against them, the threats and opportunities they create, and the date you last verified it. That last field — last verified — is what tells you whether the read is current or a year stale.' },
            { q: 'How often should you do a competitive analysis?', a: 'Continuously in spirit, formally each quarter or before a major launch or positioning decision. The trap is treating it as a one-time deliverable: a rival reprices or repositions and the analysis is quietly wrong, so the durable approach is to update the competitor you touched rather than rebuild the whole document.' },
            { q: 'How does Draftlize keep a competitive analysis current?', a: 'Each competitor is a card linked to the positioning decision it informs. Update a rival after they reprice and every decision or spec that rested on the old read flags itself stale — so your differentiation cannot keep quietly resting on a number that is no longer true.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Start your competitive analysis <em>free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Take the template above, or spin up a project and let the agent fill it in from your existing research — then keep every competitor read, and the positioning that depends on it, current for you as the market moves.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
      <TemplateCta template="competitive-analysis" />
    </MarketingShell>
  );
}
